import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import Lottie from 'lottie-react';
import nuevaVentaAnimation from '../assets/nueva-venta.json';
import inventoryAnimation from '../assets/inventory.json';
import addFriendAnimation from '../assets/add-friend.json';
import analyticalAnimation from '../assets/analytical-representation.json';
import businessmanGrowthAnimation from '../assets/businessman-growth.json';

const DashboardContainer = styled.div`
  padding: ${props => props.theme.spacing.lg};
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.md};
  }
`;

const Header = styled.div`
  margin-bottom: ${props => props.theme.spacing.sm};
  padding: 4px 0;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    margin-bottom: ${props => props.theme.spacing.xs};
  }
  
  h1 {
    margin: 0 0 2px 0;
    font-size: ${props => props.theme.typography.fontSize.xl};
    line-height: 1.1;
    
    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      font-size: ${props => props.theme.typography.fontSize.lg};
    }
  }
  
  p {
    color: ${props => props.theme.colors.text.secondary};
    margin: 0;
    font-size: ${props => props.theme.typography.fontSize.sm};
    line-height: 1.2;
    
    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      font-size: ${props => props.theme.typography.fontSize.xs};
    }
  }
`;


const QuickActions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.xl};
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${props => props.theme.spacing.sm};
    margin-bottom: ${props => props.theme.spacing.lg};
  }
`;

const variantPalette = {
  success: { from: '#34D399', to: '#059669', glow: '5, 150, 105' },
  danger:  { from: '#FB7185', to: '#E11D48', glow: '225, 29, 72' },
  primary: { from: '#60A5FA', to: '#2563EB', glow: '37, 99, 235' },
  warning: { from: '#FCD34D', to: '#D97706', glow: '217, 119, 6' },
  orange:  { from: '#FB923C', to: '#EA580C', glow: '234, 88, 12' },
  info:    { from: '#94A3B8', to: '#475569', glow: '71, 85, 105' }
};

const getPalette = v => variantPalette[v] || variantPalette.info;

const ActionCard = styled.div`
  position: relative;
  padding: 20px 16px 18px;
  text-align: center;
  cursor: pointer;
  text-decoration: none;
  color: #fff;
  overflow: hidden;
  isolation: isolate;
  border-radius: 22px;
  background: ${props => `linear-gradient(135deg, ${getPalette(props.variant).from} 0%, ${getPalette(props.variant).to} 100%)`};
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.35) inset,
    0 -1px 0 rgba(0, 0, 0, 0.08) inset,
    0 6px 14px -4px rgba(${props => getPalette(props.variant).glow}, 0.55),
    0 18px 40px -12px rgba(${props => getPalette(props.variant).glow}, 0.35),
    0 2px 6px rgba(15, 23, 42, 0.08);
  transition:
    transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1),
    box-shadow 220ms ease,
    filter 220ms ease;
  will-change: transform, box-shadow;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: radial-gradient(
      circle at 30% 0%,
      rgba(255, 255, 255, 0.35) 0%,
      rgba(255, 255, 255, 0) 55%
    );
    pointer-events: none;
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    border: 1px solid rgba(255, 255, 255, 0.22);
    pointer-events: none;
    z-index: 2;
  }

  & > * {
    position: relative;
    z-index: 3;
  }

  &:hover {
    transform: translateY(-4px) scale(1.015);
    filter: brightness(1.05) saturate(1.08);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.45) inset,
      0 -1px 0 rgba(0, 0, 0, 0.08) inset,
      0 10px 22px -6px rgba(${props => getPalette(props.variant).glow}, 0.65),
      0 28px 60px -16px rgba(${props => getPalette(props.variant).glow}, 0.5),
      0 4px 10px rgba(15, 23, 42, 0.12);
  }

  &:active {
    transform: translateY(-1px) scale(0.99);
    transition-duration: 90ms;
  }

  p {
    margin: 10px 0 0;
    font-size: 15px;
    font-weight: 700;
    color: #fff;
    letter-spacing: 0.3px;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.22);
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 14px 10px 12px;
    border-radius: 18px;

    p {
      font-size: 13px;
      margin-top: 6px;
    }
  }
`;

const LottieBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 104px;
  border-radius: 20px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.22) 0%, rgba(255, 255, 255, 0.06) 100%);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.4) inset,
    0 2px 6px rgba(0, 0, 0, 0.08);

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    height: 82px;
    border-radius: 16px;
  }
`;


export const Dashboard = () => {
  const { userData } = useAuth();

  return (
    <DashboardContainer>
      <Header>
        <h1>Bienvenido, {userData?.pharmacyName}</h1>
        <p>{format(new Date(), "EEEE, dd 'de' MMMM 'de' yyyy", { locale: es })}</p>
      </Header>

      <QuickActions>
        <ActionCard as={Link} to="/sales" variant="success">
          <LottieBadge>
            <Lottie animationData={nuevaVentaAnimation} loop style={{ width: 90, height: 90 }} />
          </LottieBadge>
          <p>Nueva Venta</p>
        </ActionCard>

        <ActionCard as={Link} to="/expenses" variant="danger">
          <LottieBadge>
            <Lottie animationData={businessmanGrowthAnimation} loop style={{ width: 90, height: 90 }} />
          </LottieBadge>
          <p>Nuevo Gasto</p>
        </ActionCard>

        <ActionCard as={Link} to="/inventory" variant="warning">
          <LottieBadge>
            <Lottie animationData={inventoryAnimation} loop style={{ width: 100, height: 100 }} />
          </LottieBadge>
          <p>Agregar Producto</p>
        </ActionCard>

        <ActionCard as={Link} to="/customers" variant="primary">
          <LottieBadge>
            <Lottie animationData={addFriendAnimation} loop style={{ width: 90, height: 90 }} />
          </LottieBadge>
          <p>Nuevo Cliente</p>
        </ActionCard>

        <ActionCard as={Link} to="/reports" variant="orange">
          <LottieBadge>
            <Lottie animationData={analyticalAnimation} loop style={{ width: 90, height: 90 }} />
          </LottieBadge>
          <p>Ver Reportes</p>
        </ActionCard>

        <ActionCard as={Link} to="/inventory?view=list" variant="info">
          <LottieBadge>
            <Lottie animationData={inventoryAnimation} loop style={{ width: 100, height: 100 }} />
          </LottieBadge>
          <p>Inventario</p>
        </ActionCard>
      </QuickActions>
    </DashboardContainer>
  );
};